<template>
  <Transition name="fade">
    <Dialog
      v-if="isOpen"
      title="Copy Order"
      :showCloseButton="true"
      :showFooter="true"
      :showSaveButton="true"
      :showCancelButton="true"
      md
      @closeEvent="closeModal"
      @cancelEvent="closeModal"
      @saveEvent="submitCopyOrder"
      :saveButtonProps="{ disabled: false }"
      :saveButtonDisabled="isSaveDisabled"
    >
      <template #modal-content>
        <div class="flex flex-col gap-3">
          <!-- Order Admin -->
          <div class="border-b pb-3 mb-1">
            <h2 class="text-lg font-semibold mb-2">Order Admin</h2>
            <div
              v-if="isOriginalAdminActive && originalAdmin"
              class="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-md px-3 py-2"
            >
              <span class="font-medium">{{ originalAdmin.name }}</span>
              <span class="text-gray-500"> will be auto-assigned to the copied order.</span>
            </div>
            <div v-else>
              <div
                class="text-sm text-yellow-800 bg-yellow-50 border border-yellow-300 rounded-md px-3 py-2 mb-2"
              >
                The original Order Admin is no longer active or available. Please assign an Order
                Admin manually before continuing.
              </div>
              <Select
                v-model="selectedOrderAdmin"
                :options="orderAdmins"
                label="name"
                track-by="id"
                placeholder="Select Order Admin"
                append-to-body
              />
            </div>
          </div>

          <!-- Header -->
          <div class="flex items-center justify-between border-b pb-2 mb-2">
            <h2 class="text-lg font-semibold">Select Products to Copy</h2>
            <label class="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" v-model="selectAllItems" @change="toggleSelectAllItems" />
              <span class="text-sm font-medium">Select All Items</span>
            </label>
          </div>

          <!-- Items -->
          <div v-for="item in order.items" :key="item.id" class="w-full">
            <Accordion :default-open="true">
              <template #header>
                <div class="flex items-center gap-3 w-full">
                  <input
                    type="checkbox"
                    v-model="itemSelections[item.id].selectAll"
                    class="w-4 h-4 text-primary"
                    @change="toggleItemSelectAll(item)"
                    @click="(e) => e.stopPropagation()"
                  />
                  <span class="font-medium">Item #{{ item.id }}</span>
                </div>
              </template>

              <template #content>
                <div class="grid grid-cols-3 gap-4 mt-2">
                  <div
                    v-for="fp in item.factory_products"
                    :key="fp.id"
                    role="checkbox"
                    tabindex="0"
                    :aria-checked="itemSelections[item.id].selectedProducts.includes(fp.id)"
                    @click="toggleProductSelection(item.id, fp.id)"
                    @keydown="triggerClickOnEnterOrSpace"
                    class="relative cursor-pointer border rounded-lg p-2 flex flex-col items-center transition-all duration-150"
                    :class="{
                      'border-primary bg-primary/10': itemSelections[
                        item.id
                      ].selectedProducts.includes(fp.id),
                      'border-gray-200 hover:border-primary/50': !itemSelections[
                        item.id
                      ].selectedProducts.includes(fp.id),
                    }"
                  >
                    <!-- Checkbox (optional small corner one) -->
                    <input
                      type="checkbox"
                      :checked="itemSelections[item.id].selectedProducts.includes(fp.id)"
                      class="absolute top-2 left-2 w-4 h-4 text-primary pointer-events-none"
                    />

                    <!-- Product Info -->
                    <div class="grid grid-cols-2 gap-2 w-full">
                      <div class="flex flex-col items-center">
                        <img
                          v-if="
                            fp.front_image &&
                            !copyOrderFpThumbFailed[copyOrderThumbKey(item.id, fp.id, 'f')]
                          "
                          :src="storage_url + '/' + fp.front_image"
                          alt="front"
                          class="size-[100px] object-cover rounded-md border border-gray-200"
                          @error="
                            copyOrderFpThumbFailed[copyOrderThumbKey(item.id, fp.id, 'f')] = true
                          "
                        />
                        <div
                          v-else
                          class="size-[100px] shrink-0 rounded-md border border-gray-200 bg-white"
                          aria-hidden="true"
                        />
                        <span class="text-xs mt-1 text-gray-600">Front</span>
                      </div>
                      <div class="flex flex-col items-center">
                        <img
                          v-if="
                            fp.back_image &&
                            !copyOrderFpThumbFailed[copyOrderThumbKey(item.id, fp.id, 'b')]
                          "
                          :src="storage_url + '/' + fp.back_image"
                          alt="back"
                          class="size-[100px] object-cover rounded-md border border-gray-200"
                          @error="
                            copyOrderFpThumbFailed[copyOrderThumbKey(item.id, fp.id, 'b')] = true
                          "
                        />
                        <div
                          v-else
                          class="size-[100px] shrink-0 rounded-md border border-gray-200 bg-white"
                          aria-hidden="true"
                        />
                        <span class="text-xs mt-1 text-gray-600">Back</span>
                      </div>
                    </div>

                    <p class="text-sm font-medium mt-2 text-center">
                      {{
                        (fp.new_product_name ? fp.new_product_name : fp.product_name) ||
                        `Product #${fp.id}`
                      }}
                    </p>
                  </div>
                </div>
              </template>
            </Accordion>
          </div>
        </div>
      </template>
    </Dialog>
  </Transition>
</template>

<script setup lang="ts">
import Accordion from '@/components/Shared/Accordion.vue';
import Dialog from '@/components/Shared/Dialog.vue';
import Select from '@/components/Shared/Select.vue';
import { showAlert, showToast } from '@/composables/swal';
import { handleResponseException, http } from '@/interceptors/axiosRequestInterceptor';
import {
  CopyOrderResponseData,
  OrderAdminAssignment,
  OrderAdminUser,
  OrderDetail,
  OrderDetailItem,
} from '@/models';
import { useMainStore } from '@/store';
import { triggerClickOnEnterOrSpace } from '@/utils/a11y';
import { storeToRefs } from 'pinia';
import { computed, onMounted, reactive, ref } from 'vue';

const storage_url = import.meta.env.VITE_APP_FILE_URL;
const props = defineProps<{ order: OrderDetail }>();
const isSubmitting = ref(false);

const store = useMainStore();
const { orderAdmins } = storeToRefs(store);

const isOpen = ref(true);
const selectAllItems = ref(false);
const itemSelections = reactive<
  Record<string, { selectAll: boolean; selectedProducts: (string | number)[] }>
>({});

const selectedOrderAdmin = ref<OrderAdminUser | null>(null);
/** Failed thumb loads → empty slot (no `/img/placeholder.png`). */
const copyOrderFpThumbFailed = reactive<Record<string, boolean>>({});
function copyOrderThumbKey(
  itemId: string | number,
  fpId: string | number,
  side: 'f' | 'b',
): string {
  return `${itemId}-${fpId}-${side}`;
}

const emit = defineEmits(['close', 'save']);

const originalAdmin = computed<OrderAdminUser | null>(() => {
  const id = props.order?.order_administrator_id;
  if (id === null || id === undefined || id === '') return null;
  return orderAdmins.value.find((a) => String(a.id) === String(id)) ?? null;
});

const isOriginalAdminActive = computed(() => originalAdmin.value !== null);

const isSaveDisabled = computed(
  () => isSubmitting.value || (!isOriginalAdminActive.value && !selectedOrderAdmin.value)
);

onMounted(async () => {
  if (!orderAdmins.value || orderAdmins.value.length === 0) {
    await store.getOrderAdmins(['orderAdministrator', 'leadOrderAdministrator']);
  }
});

// ✅ Initialize selections before render
if (props.order?.items?.length) {
  props.order.items.forEach((item) => {
    itemSelections[item.id] = {
      selectAll: false,
      selectedProducts: [],
    };
  });
}

/* -------------------------------------------------------------------------- */
/* ✅ FIX: Explicit "Select All Items" toggle instead of watcher               */
/* -------------------------------------------------------------------------- */
const toggleSelectAllItems = () => {
  const newVal = selectAllItems.value;
  props.order.items.forEach((item) => {
    const itemSel = itemSelections[item.id];
    if (!itemSel) return;
    itemSel.selectAll = newVal;
    itemSel.selectedProducts = newVal ? item.factory_products.map((fp) => fp.id) : [];
  });
};

/* -------------------------------------------------------------------------- */
const toggleItemSelectAll = (item: OrderDetailItem) => {
  const itemSel = itemSelections[item.id];
  if (!itemSel) return;
  itemSel.selectedProducts = itemSel.selectAll ? item.factory_products.map((fp) => fp.id) : [];

  // 🧠 Sync global "Select All Items" if needed
  updateGlobalSelectAll();
};

const toggleProductSelection = (itemId: string | number, productId: string | number) => {
  const itemSel = itemSelections[itemId];
  if (!itemSel) return;
  const index = itemSel.selectedProducts.indexOf(productId);
  if (index === -1) {
    itemSel.selectedProducts.push(productId);
  } else {
    itemSel.selectedProducts.splice(index, 1);
  }

  // auto toggle "select all" for that item
  const total = props.order.items.find((i) => i.id === itemId)?.factory_products.length || 0;
  itemSel.selectAll = itemSel.selectedProducts.length === total;

  // 🧠 Sync global "Select All Items"
  updateGlobalSelectAll();
};

// 🧩 Keep top-level selectAllItems reactive (without triggering resets)
const updateGlobalSelectAll = () => {
  const allSelected = props.order.items.every((item) => itemSelections[item.id]?.selectAll);
  selectAllItems.value = allSelected;
};

/* -------------------------------------------------------------------------- */
const resolveAdminIdToAssign = (): string | number | null => {
  if (isOriginalAdminActive.value && originalAdmin.value) {
    return originalAdmin.value.id;
  }
  return selectedOrderAdmin.value?.id ?? null;
};

const buildPayload = () => {
  const adminId = resolveAdminIdToAssign();
  const base: Record<string, any> = {};
  if (adminId !== null && adminId !== undefined) {
    base.order_administrator_id = adminId;
  }
  if (selectAllItems.value) return base;
  const map: Record<string, (string | number)[]> = {};
  for (const item of props.order.items) {
    const selected = itemSelections[item.id]?.selectedProducts ?? [];
    if (selected.length > 0) {
      map[item.id] = selected;
    }
  }
  return { ...base, order_item_factory_products_map: map };
};

const extractNewOrderId = (resp: any): number | string | null => {
  const data = resp?.data;
  const result = data?.result;
  if (Array.isArray(result) && result.length >= 2 && result[1]?.id != null) {
    return result[1].id;
  }
  return (
    result?.order?.id ??
    result?.id ??
    data?.order?.id ??
    data?.new_order_id ??
    data?.id ??
    null
  );
};

/**
 * Builds the warning message for a copied order that ended up with no
 * Order Admin. Returns `null` when the backend response doesn't indicate a
 * missing-admin condition (either `assigned: true` or a legacy response
 * without the `order_admin_assignment` key at all).
 */
function buildNoOrderAdminWarning(
  assignment: OrderAdminAssignment | undefined
): string | null {
  if (!assignment || assignment.assigned) return null;

  let body: string;
  if (assignment.alert_reason === 'original_admin_inactive') {
    body =
      "The copied order was created without an Order Admin because the original order's admin is no longer active.";
  } else if (assignment.alert_reason === 'original_unassigned') {
    body =
      'The copied order was created without an Order Admin because the original order had none assigned.';
  } else {
    body = 'The copied order was created without an Order Admin.';
  }

  if (assignment.alert_sent) {
    body += ' An email alert has been sent to the configured recipients.';
  }

  return body;
}

const submitCopyOrder = async () => {
  if (!isOriginalAdminActive.value && !selectedOrderAdmin.value) {
    showToast('Please assign an Order Admin before copying.', 'error');
    return;
  }

  isSubmitting.value = true;
  const payload = buildPayload();
  const adminIdToAssign = resolveAdminIdToAssign();
  try {
    const resp = await http.post<CopyOrderResponseData>(
      `/copy-order/${props.order.id}`,
      Object.keys(payload).length ? payload : {}
    );

    const newOrderId = extractNewOrderId(resp);
    if (adminIdToAssign !== null && newOrderId !== null) {
      try {
        await store.updateOrderAdmin({ user_id: adminIdToAssign, order_ids: newOrderId });
      } catch {
        // updateOrderAdmin surfaces its own toast on failure
      }
    }

    showToast(resp.data.message || 'Order copied successfully!', 'success');
    emit('save', true);
    closeModal();

    const warningMessage = buildNoOrderAdminWarning(resp.data.result?.order_admin_assignment);
    if (warningMessage) {
      showAlert('No Order Admin assigned', warningMessage, 'warning');
    }
  } catch (err: any) {
    handleResponseException(err);
    isSubmitting.value = false;
  }
};

const closeModal = () => {
  emit('close');
};
</script>

<style scoped lang="scss">
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
